import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const ServicePageTemplate = ({ 
  title, 
  subtitle, 
  description, 
  image, 
  services, 
  features,
  relatedServices 
}) => {
  return (
    <div data-testid={`service-page-${title.toLowerCase().replace(/\s/g, '-')}`}>
      {/* Hero Section */}
      <section className="relative py-24 bg-navy">
        <div className="absolute inset-0 opacity-20">
          <img src={image} alt={title} className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="font-manrope text-cyan mb-4">{subtitle}</p>
            <h1 className="font-oswald uppercase text-5xl md:text-6xl font-bold text-white mb-6">
              {title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={image} 
                alt={title}
                className="rounded-md shadow-xl w-full"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title text-3xl md:text-4xl mb-6">{title}</h2>
              <p className="font-manrope text-slate-600 mb-8 leading-relaxed text-lg">
                {description}
              </p>

              {features && (
                <div className="space-y-4 mb-8">
                  {features.map((feature) => (
                    <div key={feature} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-cyan flex-shrink-0 mt-0.5" />
                      <span className="font-manrope text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="btn-primary" data-testid="service-contact-btn">
                    Contact Us
                  </Button>
                </Link>
                <a href="tel:651-738-9354">
                  <Button className="btn-outline" data-testid="service-call-btn">
                    <Phone className="w-5 h-5 mr-2" />
                    651-738-9354
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services List */}
      {services && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="section-title text-4xl mb-4">Our {title} Services</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((service, index) => (
                <motion.div
                  key={service}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center gap-3 p-4 bg-white rounded-md shadow-sm"
                >
                  <ArrowRight className="w-5 h-5 text-orange flex-shrink-0" />
                  <span className="font-manrope text-slate-700">{service}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Services */}
      {relatedServices && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="section-title text-4xl mb-4">Related Services</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedServices.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link 
                    to={service.link}
                    className="block p-6 bg-slate-50 rounded-md hover:bg-slate-100 transition-colors group"
                  >
                    <h3 className="font-oswald uppercase text-xl font-bold text-navy group-hover:text-orange transition-colors mb-2">
                      {service.title}
                    </h3>
                    <p className="font-manrope text-slate-600 text-sm">{service.description}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-oswald uppercase text-4xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="font-manrope text-slate-300 text-xl mb-8">
              Contact us today. We're here to help!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="btn-primary text-lg">
                  Contact Us
                </Button>
              </Link>
              <a href="tel:651-738-9354">
                <button className="bg-white text-[#14366C] hover:bg-gray-100 font-oswald font-bold uppercase tracking-wider px-8 py-4 rounded-md text-lg flex items-center transition-colors">
                  <Phone className="w-5 h-5 mr-2" />
                  651-738-9354
                </button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicePageTemplate;
